import SwimFormField from '@/components/shared/form/SwimFormField'; // Added SwimFormField
import { ModalProps, StyledDialog } from '@/components/shared/StyledDialog';
import { Form } from '@/components/ui/form'; // Added Form
import { toast } from '@/hooks/useToast';
import { useSupabaseFunction } from '@/services/supabase';
import { functions } from '@/types/functions.types';
import { Property } from '@/types/property';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react'; // Added useState
import { useForm } from 'react-hook-form';
import { z } from 'zod';

interface ContactSellerFormProps extends ModalProps {
    property: Property;
}

const contactFormSchema = functions['contact-seller'].input;
type ContactFormValues = z.infer<typeof contactFormSchema>;

export function ContactSellerForm({ property, open, setOpen }: ContactSellerFormProps) {
    
    const form = useForm<ContactFormValues>({
        resolver: zodResolver(contactFormSchema),
        defaultValues: {
            buyerName: '',
            buyerEmail: '',
            buyerPhone: '',
            message: '',
            propertyId: property.property_id,
        },
    });

    useEffect(() => {
        if (!open) {
            form.reset();
        };
    }, [open, form]);

    const contactSellerFunction = useSupabaseFunction('contact-seller');

    const onSubmit = async (data: ContactFormValues) => {
        await contactSellerFunction.mutateAsync(data, {
            onError: (error) => {
                toast({
                    title: 'Error Sending Message',
                    description: error.message || 'There was a problem sending your message. Please try again.',
                    variant: 'destructive',
                });
            },
            onSuccess: () => {
                toast({
                    title: 'Message Sent!',
                    description: 'Your inquiry has been sent to the seller.',
                });
                form.reset();
                setOpen(false);
            },
        });
    };

    return (
        <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
                <StyledDialog
                    open={open}
                    setOpen={setOpen}
                    title={`Contact Seller for ${property.street_name}`}
                    description="Send an inquiry to the property seller."
                    maxWidth="lg"
                    content={
                        <>
                            <SwimFormField
                                schema={contactFormSchema}
                                fieldName="buyerName"
                                label="Your Name"
                                placeholder="John Doe"
                            />
                            <SwimFormField
                                schema={contactFormSchema}
                                fieldName="buyerEmail"
                                label="Your Email"
                                placeholder="you@example.com"
                            />
                            <SwimFormField
                                schema={contactFormSchema}
                                fieldName="buyerPhone"
                                label="Your Phone Number"
                                placeholder="(555) 555-5555"
                            />
                            <SwimFormField
                                schema={contactFormSchema}
                                fieldName="message"
                                label="Message"
                                placeholder={`I am interested in the property at ${property.street_name}...`}
                                className="h-32"
                            />
                        </>
                    }
                    footerButtons={{
                        primary: {
                            text: contactSellerFunction.isPending ? 'Sending...' : 'Send Message',
                            onClick: () => form.handleSubmit(onSubmit)(),
                            loading: contactSellerFunction.isPending,
                            disabled: contactSellerFunction.isPending,
                        },
                    }}
                />
            </form>
        </Form>
    );
}